import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Smartphone, Monitor, Tablet, Clock, Cloud, Zap, Signal, CheckCircle, Plus, ArrowRight } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { PageLayout } from '@/components/layout/PageLayout';
import {
  registerCurrentDevice,
  fetchMobileCompanionDevices,
  fetchMobileCompanionStats,
  registerMobileCompanionDevice,
  syncMobileCompanion,
  sendMobileHandoff,
  type MobileCompanionDevice,
} from '../services/sync-share-service';

interface ConnectedDevice {
  id: string;
  name: string;
  type: 'phone' | 'tablet' | 'laptop' | 'desktop';
  lastActive: Date;
  batteryLevel?: number;
  syncEnabled: boolean;
}

function getRelativeTime(date: Date) {
  const diff = Date.now() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function mapDeviceType(type?: string, platform?: string): ConnectedDevice['type'] {
  const value = (type ?? platform ?? '').toLowerCase();
  if (value.includes('phone') || value === 'ios' || value === 'android' || value === 'mobile') return 'phone';
  if (value.includes('tablet') || value === 'ipad') return 'tablet';
  if (value.includes('laptop')) return 'laptop';
  return 'desktop';
}

function toConnectedDevice(d: MobileCompanionDevice): ConnectedDevice {
  const lastSeen = d.last_active ?? d.last_seen ?? new Date().toISOString();
  return {
    id: d.id,
    name: d.name,
    type: mapDeviceType(d.type, d.platform),
    lastActive: new Date(lastSeen),
    batteryLevel: d.battery_level,
    syncEnabled: d.sync_enabled ?? d.is_online ?? true,
  };
}

const MobileCompanion = () => {
  const [selectedDevice, setSelectedDevice] = useState<ConnectedDevice | null>(null);
  const [devices, setDevices] = useState<ConnectedDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ lastSync: 'Never', mobileSessions: 0 });
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      await registerCurrentDevice().catch(() => undefined);
      const [deviceList, companionStats] = await Promise.all([
        fetchMobileCompanionDevices(),
        fetchMobileCompanionStats().catch(() => null),
      ]);
      setDevices(deviceList.map(toConnectedDevice));
      if (companionStats) {
        const lastSync = companionStats.last_sync
          ? getRelativeTime(new Date(companionStats.last_sync))
          : 'Never';
        setStats({ lastSync, mobileSessions: companionStats.mobile_sessions ?? 0 });
      }
    } catch (err) {
      console.error('Failed to load mobile companion data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddDevice = async () => {
    const name = window.prompt('Device name', 'My Phone');
    if (!name?.trim()) return;
    try {
      await registerMobileCompanionDevice(name.trim(), 'phone');
      await loadData();
      setActionMessage('Device added');
    } catch (err) {
      console.error('Failed to add device:', err);
      setActionMessage('Failed to add device');
    }
  };

  const handleSyncAll = async () => {
    try {
      await syncMobileCompanion();
      await loadData();
      setActionMessage('Sync complete');
    } catch (err) {
      console.error('Sync failed:', err);
      setActionMessage('Sync failed');
    }
  };

  const handleSendToDevice = async (targetType: 'phone' | 'laptop') => {
    const target = devices.find(d => (targetType === 'phone' ? d.type === 'phone' : d.type === 'laptop' || d.type === 'desktop'));
    if (!target) {
      setActionMessage(`No ${targetType} device connected`);
      return;
    }
    try {
      await sendMobileHandoff(target.id);
      setActionMessage(`Sent to ${target.name}`);
    } catch (err) {
      console.error('Handoff failed:', err);
      setActionMessage('Handoff failed');
    }
  };

  const getDeviceIcon = (type: string) => {
    switch (type) {
      case 'phone': return Smartphone;
      case 'tablet': return Tablet;
      case 'laptop': return Monitor;
      case 'desktop': return Monitor;
      default: return Smartphone;
    }
  };

  return (
    <PageLayout title="Mobile Companion" description="Manage connected devices and sync settings" icon={Smartphone}>
      <div className="mb-8 flex justify-end">
        <button
          onClick={handleAddDevice}
          className="flex items-center space-x-2 px-6 py-3 bg-primary-500 text-white rounded-xl font-medium shadow-lg"
        >
          <Plus className="h-5 w-5" />
          <span>Add Device</span>
        </button>
      </div>

      {actionMessage && (
        <p className="mb-4 text-sm text-khiimori-text-secondary">{actionMessage}</p>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Connected Devices', value: devices.length, textClass: 'text-primary-500' },
          { label: 'Sync Active', value: devices.filter(d => d.syncEnabled).length, textClass: 'text-success-500' },
          { label: 'Last Sync', value: stats.lastSync, textClass: 'text-info-500' },
          { label: 'Mobile Sessions', value: stats.mobileSessions, textClass: 'text-khiimori-accent-violet' },
        ].map((stat, index) => (
          <motion.div key={index} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }}>
            <Card hover>
              <div className="text-center">
                <div className={`text-2xl font-bold ${stat.textClass}`}>
                  {stat.value}
                </div>
                <div className="text-sm text-khiimori-text-secondary">{stat.label}</div>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div>
          <h2 className="text-lg font-semibold text-khiimori-text-primary mb-4">Your Devices</h2>
          {loading && (
            <div className="text-center text-khiimori-text-muted py-8">Loading devices...</div>
          )}
          {!loading && devices.length === 0 && (
            <div className="text-center text-khiimori-text-muted py-8">
              No devices yet. Add a device or refresh this page to register your browser.
              <button onClick={loadData} className="block mx-auto mt-4 px-4 py-2 bg-primary-500 text-white rounded-lg">
                Refresh
              </button>
            </div>
          )}
          <div className="space-y-4">
            {devices.map((device, index) => {
              const DeviceIcon = getDeviceIcon(device.type);
              return (
                <motion.div key={device.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}>
                  <Card hover className={`cursor-pointer ${selectedDevice?.id === device.id ? 'ring-2 ring-primary-500' : ''}`} onClick={() => setSelectedDevice(device)}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 flex-1">
                        <div className={`p-3 rounded-xl ${device.syncEnabled ? 'bg-primary-500' : 'bg-khiimori-bg-tertiary'}`}>
                          <DeviceIcon className="h-6 w-6 text-khiimori-text-primary" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-khiimori-text-primary">{device.name}</h3>
                          <div className="flex items-center space-x-4 text-sm text-khiimori-text-secondary">
                            <span className="flex items-center"><Clock className="h-4 w-4 mr-1" /> {getRelativeTime(device.lastActive)}</span>
                            {device.batteryLevel != null && (
                              <span className="flex items-center"><Zap className="h-4 w-4 mr-1" /> {device.batteryLevel}%</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${device.syncEnabled ? 'bg-success-500/20 text-success-500' : 'bg-khiimori-bg-tertiary text-khiimori-text-secondary'}`}>
                          {device.syncEnabled ? 'Synced' : 'Offline'}
                        </span>
                        <ArrowRight className="h-5 w-5 text-khiimori-text-muted" />
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>

        <div>
          <Card>
            <h3 className="font-bold text-khiimori-text-primary mb-4">Quick Actions</h3>
            <div className="space-y-3">
              {[
                { icon: Cloud, label: 'Sync All Now', bgClass: 'bg-primary-500', action: handleSyncAll },
                { icon: Smartphone, label: 'Send to Phone', bgClass: 'bg-khiimori-accent-violet', action: () => handleSendToDevice('phone') },
                { icon: Monitor, label: 'Send to Laptop', bgClass: 'bg-info-500', action: () => handleSendToDevice('laptop') },
                { icon: Signal, label: 'Check Connection', bgClass: 'bg-success-500', action: loadData },
              ].map((action, index) => (
                <button
                  key={index}
                  onClick={action.action}
                  className="w-full flex items-center space-x-3 p-4 bg-khiimori-bg-secondary rounded-xl hover:bg-khiimori-bg-elevated transition-colors"
                >
                  <div className={`p-2 rounded-lg ${action.bgClass}`}>
                    <action.icon className="h-5 w-5 text-khiimori-text-primary" />
                  </div>
                  <span className="font-medium text-khiimori-text-primary">{action.label}</span>
                </button>
              ))}
            </div>
          </Card>
        </div>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
        <Card className="bg-gradient-glass border-primary-400/30 dark:border-primary-400/20">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-primary-500 rounded-xl">
              <Smartphone className="h-6 w-6 text-khiimori-text-primary" />
            </div>
            <div>
              <h3 className="font-bold text-khiimori-text-primary">Mobile App Status</h3>
              <p className="text-sm text-khiimori-text-secondary">
                {devices.length > 0 ? 'All devices connected and synchronized' : 'Register a device to start syncing'}
              </p>
            </div>
            <div className="ml-auto flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full animate-pulse ${devices.length > 0 ? 'bg-success-500' : 'bg-warning-500'}`}></div>
              <span className={`text-sm font-medium ${devices.length > 0 ? 'text-success-500 dark:text-success-400' : 'text-warning-500'}`}>
                {devices.length > 0 ? 'Connected' : 'Waiting'}
              </span>
            </div>
          </div>
        </Card>
      </motion.div>
    </PageLayout>
  );
};

export default MobileCompanion;
